import type { EntityStore } from '@datorama/akita';
import { applyTransaction, getEntityType, getIDType } from '@datorama/akita';
import { GitQuery } from '@y42/git';
import { Y42FeatureType } from '@y42/json-schema';
import { ToastService } from '@y42/toast';
import { takePromise } from '@y42/util/take-promise';
import { of } from 'rxjs';
import { AbstractFeatureAclService } from '../acl';
import { AbstractFeatureGitService } from '../git';
import { AbstractGitEntityStore } from '../git/state/abstract-git-entity-store.service';
import { AbstractFeatureEntityState } from '../models';
import { AbstractFeatureEntityStore, AbstractFeatureQueryEntity } from '../state';
import { AbstractUserRelatedDataService } from './abstract-user-related-data.service';

export abstract class AbstractFeatureService<
  S extends AbstractFeatureEntityState = AbstractFeatureEntityState,
  Entity extends getEntityType<S> = getEntityType<S>,
> {
  constructor(
    public readonly config: {
      featureType: Y42FeatureType;
      store: AbstractFeatureEntityStore<S>;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      query: AbstractFeatureQueryEntity<S, any>;
      git: AbstractFeatureGitService;
      acl?: AbstractFeatureAclService;
      userData: AbstractUserRelatedDataService<S> | null;

      gitQuery: GitQuery;
      toast: ToastService;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      childStores: Array<AbstractGitEntityStore<any, any, any> | EntityStore<any, any, any>>;
    },
  ) {}

  async getAll(options?: { setLoading?: boolean }): Promise<void> {
    const { setLoading } = options || {};
    const { activeIdByStore } = this.extractCurrentSubState();
    const allGitStores = [this.config.store, ...this.config.childStores].filter(
      (store) => store instanceof AbstractGitEntityStore,
    ) as AbstractGitEntityStore[];

    const allNonGitStores = [this.config.store, ...this.config.childStores].filter(
      (store) => !(store instanceof AbstractGitEntityStore),
    ) as EntityStore[];

    if (setLoading) {
      this.config.store.setLoading(true);
    }

    await applyTransaction(async () => {
      // remove non-existent entities from stores
      allGitStores.map((store) => {
        const idsToRemove = ((store.getValue().ids || []) as string[]).filter(
          ($path) => !this.config.gitQuery.elementAtPathExists($path),
        );

        if (idsToRemove.length) {
          store.remove(idsToRemove, { hideWarning: true });
        }
      });

      allNonGitStores.forEach((store) => store.reset());

      try {
        const [entitiesByLevel] = await Promise.all([
          this.config.git.getAll(),
          takePromise(this.config.acl?.loadResourcesWithReadPermissions() || of(null)),
        ]);
        allGitStores.forEach((store, level) => {
          const entity = entitiesByLevel[level];
          if (entity) {
            store.upsertManyWithoutSync(entity);
          }
        });
      } catch (err) {
        console.error(err);
        this.config.toast.error(err instanceof Error ? err.message : `${err}`);
      } finally {
        this.restoreActiveIds(activeIdByStore);

        if (setLoading) {
          this.config.store.setLoading(false);
        }
      }
    });
  }

  async get($name: Entity['$name'], options?: { ignoreErrors?: boolean }): Promise<null | Entity> {
    const allGitStores = [this.config.store, ...this.config.childStores].filter(
      (store) => store instanceof AbstractGitEntityStore,
    ) as AbstractGitEntityStore[];
    const { ignoreErrors = false } = options || {};
    const { git, toast } = this.config;
    let model: Entity | null = null;

    try {
      const entitiesByLevel = (await git.get($name)) as Entity[][];
      allGitStores.forEach((store, level) => {
        const entity = entitiesByLevel[level];
        if (entity) {
          store.upsertManyWithoutSync(entity);
        }
      });
      model = (entitiesByLevel[0] || null)?.[0] ?? null;
    } catch (err) {
      if (ignoreErrors) {
        return null;
      }
      console.error(err);
      toast.error(err instanceof Error ? err.message : `${err}`);
    }

    if (!model) {
      this.config.store.remove($name);
    }

    return model;
  }

  abstract create(item: unknown): Promise<Entity | void>;
  abstract update(item: Partial<Entity>, $path?: Entity['$path']): Promise<void>;
  abstract delete($path?: Entity['$path']): Promise<void>;

  setActive($path: Entity['$path'] | null): void {
    this.config.store.setActive($path);
  }

  async toggleStar($path = this.config.query.getActiveId() as getIDType<S>) {
    await this.config.userData?.toggleStarred($path);
  }

  async setLastUsed($path = this.config.query.getActiveId() as getIDType<S>) {
    await this.config.userData?.setLastUsed($path);
  }

  async refreshLastUsed() {
    if (!this.config.userData) {
      return;
    }

    return this.config.userData.getLastUsed();
  }

  async refreshStarred() {
    if (!this.config.userData) {
      return;
    }

    return this.config.userData.getStarred();
  }

  reset() {
    this.config.store.reset();
    this.config.childStores.forEach((store) => store.reset());
  }

  // Extract all essential data from current state
  private extractCurrentSubState() {
    const storeWithChildStores = [this.config.store, ...this.config.childStores];
    const activeIdByStore = storeWithChildStores.map((store) => ({
      store,
      active: store.getValue().active as string,
    }));

    return { activeIdByStore };
  }

  // Reset previously active entities if still present
  private restoreActiveIds(activeIdByStore: Array<{ store: EntityStore; active: string }>) {
    activeIdByStore
      .filter(({ store, active }) => !!store.getValue().entities[active])
      .forEach(({ store, active }) => store.setActive(active));
  }
}
